import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, LockIcon, StoreIcon } from 'lucide-react';
import { products } from '../data/products';
import { useCart } from '../contexts/CartContext';
import { CartLineItem } from '../components/cart/CartLineItem';
import { FreeShippingProgress } from '../components/cart/FreeShippingProgress';
import { ProductGrid } from '../components/product/ProductGrid';
import { SectionHeading } from '../components/ui/SectionHeading';
import { btnPrimary } from '../components/ui/buttonStyles';
import { isProductLeavingSoon } from '../utils/catalog';
import { formatPrice } from '../utils/currency';
import { easeOut } from '../utils/motion';
export function Cart() {
  const {
    lines,
    subtotal,
    count
  } = useCart();
  const inCart = new Set(lines.map(l => l.productId));
  const suggestions = products.filter(p => !inCart.has(p.id)).sort((a, b) => Number(isProductLeavingSoon(b)) - Number(isProductLeavingSoon(a))).slice(0, 4);
  return <div className="mx-auto max-w-[1440px] px-5 pt-8 md:px-8 md:pt-12" data-id="element-639" data-mp="29hhqf2eomdfn">
      <h1 className="font-display text-5xl md:text-7xl" data-id="element-640" data-mp="2ebm382ampfon">
        Panier {count > 0 && <span className="text-muted" data-id="element-641" data-mp="1zm6essyklmc4">({count})</span>}
      </h1>

      {lines.length === 0 ? <div className="mt-10 rounded-sm bg-card px-6 py-20 text-center" data-id="element-642" data-mp="24l9hi6r2vggx">
          <p className="font-display text-3xl" data-id="element-643" data-mp="2s9am54l4ycr2">Votre panier est vide</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted" data-id="element-644" data-mp="2s7zj56zil910">
            Les créateurs de la saison ne restent que quelques semaines. Prenez le temps de flâner.
          </p>
          <Link to="/boutique" className={`${btnPrimary} mt-6`} data-id="element-645" data-mp="2j8um653lpnqy">
            Découvrir la boutique
          </Link>
        </div> : <div className="mt-10 grid gap-12 lg:grid-cols-12" data-id="element-646" data-mp="17u7bflj3nh9g">
          <ul className="divide-y divide-line border-y border-line lg:col-span-7" data-id="element-647" data-mp="17w1vkxmjr5ch">
            <AnimatePresence initial={false} data-id="element-648" data-mp="1bx3vzhs79ng1">
              {lines.map(line => <motion.li key={line.key} layout exit={{
            opacity: 0,
            x: 24
          }} transition={{
            duration: 0.22,
            ease: easeOut
          }} className="py-6" data-id="element-649" data-mp="3egwij5ar4jyh">
                  <CartLineItem line={line} size="large" data-id="element-650" data-mp="2ztbe98254eoz" />
                </motion.li>)}
            </AnimatePresence>
          </ul>
          <aside className="self-start lg:sticky lg:top-28 lg:col-span-5" aria-label="Récapitulatif" data-id="element-651" data-mp="2vshrwj4cj7y7">
            <div className="rounded-md bg-card p-6 md:p-8" data-id="element-652" data-mp="2qru3m57i557o">
              <FreeShippingProgress subtotal={subtotal} data-id="element-653" data-mp="2ltaczrhyr58z" />
              <dl className="mt-6 space-y-2.5 border-t border-line pt-5 text-sm" data-id="element-654" data-mp="2ltsjdgycazum">
                <div className="flex justify-between" data-id="element-655" data-mp="2gw0gxt02jvc6">
                  <dt className="text-muted" data-id="element-656" data-mp="k1xb7bbaldh5">Sous-total</dt>
                  <dd className="tabular-nums" data-id="element-657" data-mp="k1e1uizpjxeq">{formatPrice(subtotal)}</dd>
                </div>
                <div className="flex justify-between" data-id="element-658" data-mp="f3vrco61la4m">
                  <dt className="text-muted" data-id="element-659" data-mp="nkwhbzne4zv1">Livraison</dt>
                  <dd data-id="element-660" data-mp="nia8zxsoegbr">Dès 0 € (retrait)</dd>
                </div>
                <div className="flex items-baseline justify-between border-t border-line pt-4" data-id="element-661" data-mp="rkdk2lbfniwi">
                  <dt className="font-medium" data-id="element-662" data-mp="wdpxs0ekq5ah">Total estimé</dt>
                  <dd className="font-display text-3xl tabular-nums" data-id="element-663" data-mp="11c18edcp0jtr">{formatPrice(subtotal)}</dd>
                </div>
              </dl>
              <Link to="/commande" className={`${btnPrimary} mt-6 w-full`} data-id="element-664" data-mp="4mk3i0ljb1t4">
                Passer commande <ArrowRightIcon className="h-4 w-4" data-id="element-665" data-mp="8nlvpxh1adll" />
              </Link>
              <ul className="mt-5 space-y-2 text-xs text-muted" data-id="element-666" data-mp="dldxrkbe7g3i">
                <li className="flex items-center gap-2" data-id="element-667" data-mp="do9x9d2w2cu2">
                  <LockIcon className="h-3.5 w-3.5" aria-hidden data-id="element-668" data-mp="iltaqarrilky" /> Paiement sécurisé, codes promo à l’étape suivante
                </li>
                <li className="flex items-center gap-2" data-id="element-669" data-mp="1p36qz0a0pq7r">
                  <StoreIcon className="h-3.5 w-3.5" aria-hidden data-id="element-670" data-mp="1tz412ouou0c3" /> Retrait gratuit en boutique sous 24 h
                </li>
              </ul>
            </div>
          </aside>
        </div>}

      {suggestions.length > 0 && <section className="pt-20" aria-labelledby="suggestions" data-id="element-671" data-mp="2y03swem87bz8">
          <SectionHeading id="suggestions" title={<>
                Avant qu’ils <em className="text-clay" data-id="element-673" data-mp="36x5q44r6pprd">ne partent</em>
              </>} data-id="element-672" data-mp="3bylbjxx9xyaa" />
          <ProductGrid products={suggestions} data-id="element-674" data-mp="3luewa4l5cy35" />
        </section>}
    </div>;
}